import { Button, Checkbox, Chip, Icon } from "@legal-500/legal500-design-system";
import type {
  SurveyRefereeCategory,
  SurveyRefereeType,
  SurveyRegionType,
} from "@/api/surveys.service";
import styles from "./surveyConditions.module.css";

type ConditionOption<T extends string> = { value: T; label: string };

export const REGION_TYPE_OPTIONS: ConditionOption<SurveyRegionType>[] = [
  { value: "country", label: "Country" },
  { value: "bar", label: "Bar" },
  { value: "other", label: "Other" },
];

// UI label "Bar" maps to data value "barrister" — intentional: product calls
// the table type "bar", the data calls the referee "barrister".
export const REFEREE_TYPE_OPTIONS: ConditionOption<SurveyRefereeType>[] = [
  { value: "lawyer", label: "Lawyer" },
  { value: "firm", label: "Firm" },
  { value: "barrister", label: "Bar" },
  { value: "mediator", label: "Mediator" },
];

export const REFEREE_CATEGORY_OPTIONS: ConditionOption<SurveyRefereeCategory>[] = [
  { value: "instructing", label: "Instructing solicitor" },
  { value: "other", label: "Other" },
];

export type SurveyConditionValues = {
  regionTypes: SurveyRegionType[];
  refereeTypes: SurveyRefereeType[];
  refereeCategories: SurveyRefereeCategory[];
};

export const EMPTY_SURVEY_CONDITIONS: SurveyConditionValues = {
  regionTypes: [],
  refereeTypes: [],
  refereeCategories: [],
};

type PartialConditions = {
  regionTypes?: SurveyRegionType[];
  refereeTypes?: SurveyRefereeType[];
  refereeCategories?: SurveyRefereeCategory[];
};

export function toSurveyConditionValues(source?: PartialConditions | null): SurveyConditionValues {
  return {
    regionTypes: source?.regionTypes ?? [],
    refereeTypes: source?.refereeTypes ?? [],
    refereeCategories: source?.refereeCategories ?? [],
  };
}

export function hasExceptionConditions(source?: PartialConditions | null): boolean {
  return (
    (source?.regionTypes?.length ?? 0) > 0 ||
    (source?.refereeTypes?.length ?? 0) > 0 ||
    (source?.refereeCategories?.length ?? 0) > 0
  );
}

/**
 * Condition fields for create/update payloads. Empty arrays are omitted so a
 * general survey's payload stays identical to the pre-conditions payload.
 */
export function surveyConditionsToPayloadFields(
  conditions: SurveyConditionValues,
): PartialConditions {
  return {
    ...(conditions.regionTypes.length > 0 ? { regionTypes: conditions.regionTypes } : {}),
    ...(conditions.refereeTypes.length > 0 ? { refereeTypes: conditions.refereeTypes } : {}),
    ...(conditions.refereeCategories.length > 0
      ? { refereeCategories: conditions.refereeCategories }
      : {}),
  };
}

function canonicalizeConditionList<T extends string>(values?: T[] | null): T[] {
  return [...new Set(values ?? [])].sort();
}

function conditionListsEqual<T extends string>(a?: T[] | null, b?: T[] | null): boolean {
  const canonicalA = canonicalizeConditionList(a);
  const canonicalB = canonicalizeConditionList(b);
  return (
    canonicalA.length === canonicalB.length &&
    canonicalA.every((value, index) => value === canonicalB[index])
  );
}

/**
 * True when both sides describe the same condition combination after
 * canonicalization (sort + dedupe, undefined -> []) — mirroring the
 * surveyConditionsToPayloadFields semantics and the backend's uniqueness
 * rule for (edition, publication, conditions).
 */
export function surveyConditionsEqual(
  a?: PartialConditions | null,
  b?: PartialConditions | null,
): boolean {
  return (
    conditionListsEqual(a?.regionTypes, b?.regionTypes) &&
    conditionListsEqual(a?.refereeTypes, b?.refereeTypes) &&
    conditionListsEqual(a?.refereeCategories, b?.refereeCategories)
  );
}

/**
 * Advisory banner shown when another survey already occupies the same
 * edition + publication + condition combination. Non-blocking — the server's
 * 409 on save remains the enforcement.
 */
export function DuplicateSurveyWarning({
  survey,
}: {
  survey: PartialConditions & { name: string; status: string };
}) {
  return (
    <div className={styles.duplicateWarning} role="alert">
      <Icon icon="exclamation" size="small" />
      <span>
        {hasExceptionConditions(survey)
          ? "A survey with these exception conditions already exists for this publication: "
          : "A general survey already exists for this publication: "}
        {survey.name} ({survey.status}).
      </span>
    </div>
  );
}

function selectedLabels<T extends string>(
  values: T[] | undefined,
  options: ConditionOption<T>[],
): string {
  // Keep option order so chips read consistently regardless of click order.
  return options
    .filter(option => values?.includes(option.value))
    .map(option => option.label)
    .join(", ");
}

/**
 * Compact chips summarising a survey's exception conditions, e.g.
 * "Table: Bar, Mediator". Renders a subtle "General" label when the survey
 * has no conditions.
 */
export function SurveyConditionChips({ conditions }: { conditions?: PartialConditions | null }) {
  const groups = [
    { prefix: "Region", text: selectedLabels(conditions?.regionTypes, REGION_TYPE_OPTIONS) },
    { prefix: "Table", text: selectedLabels(conditions?.refereeTypes, REFEREE_TYPE_OPTIONS) },
    {
      prefix: "Referee",
      text: selectedLabels(conditions?.refereeCategories, REFEREE_CATEGORY_OPTIONS),
    },
  ].filter(group => group.text.length > 0);

  if (groups.length === 0) {
    return <span className={styles.generalLabel}>General</span>;
  }

  return (
    <div className={styles.chips}>
      {groups.map(group => (
        <Chip key={group.prefix} size="small" variant="outlined">
          {group.prefix}: {group.text}
        </Chip>
      ))}
    </div>
  );
}

type ConditionCheckboxGroupProps<T extends string> = {
  label: string;
  options: ConditionOption<T>[];
  selected: T[];
  onChange: (selected: T[]) => void;
  disabled?: boolean;
};

function ConditionCheckboxGroup<T extends string>({
  label,
  options,
  selected,
  onChange,
  disabled = false,
}: ConditionCheckboxGroupProps<T>) {
  const toggle = (value: T) => {
    const next = new Set(selected);
    if (next.has(value)) {
      next.delete(value);
    } else {
      next.add(value);
    }
    // Preserve option order in the emitted value.
    onChange(options.filter(option => next.has(option.value)).map(option => option.value));
  };

  return (
    <fieldset className={styles.group} disabled={disabled}>
      <legend className={styles.groupLabel}>{label}</legend>
      {options.map(option => (
        <Checkbox
          key={option.value}
          label={option.label}
          aria-label={option.label}
          checked={selected.includes(option.value)}
          disabled={disabled}
          onChange={() => toggle(option.value)}
        />
      ))}
    </fieldset>
  );
}

type ExceptionConditionsFieldsProps = {
  values: SurveyConditionValues;
  onChange: (values: SurveyConditionValues) => void;
  expanded: boolean;
  onToggleExpanded: () => void;
  disabled?: boolean;
};

/**
 * Collapsed-by-default "Add exception conditions" section. When every group is
 * left empty the survey stays a general survey; any selection turns it into an
 * exception survey that only applies to matching referees.
 */
export function ExceptionConditionsFields({
  values,
  onChange,
  expanded,
  onToggleExpanded,
  disabled = false,
}: ExceptionConditionsFieldsProps) {
  return (
    <div className={styles.section}>
      <div className={styles.toggleRow}>
        <Button
          variant="text"
          size="small"
          onClick={onToggleExpanded}
          aria-expanded={expanded}
          startIcon={<Icon icon={expanded ? "chevron-up" : "chevron-down"} size="small" />}
        >
          Add exception conditions
        </Button>
      </div>
      {expanded && (
        <>
          <p className={styles.hint}>
            Leave all empty for a general survey. Selecting any condition makes this survey apply
            only to matching referees.
          </p>
          <div className={styles.groups}>
            <ConditionCheckboxGroup
              label="Region type"
              options={REGION_TYPE_OPTIONS}
              selected={values.regionTypes}
              onChange={regionTypes => onChange({ ...values, regionTypes })}
              disabled={disabled}
            />
            <ConditionCheckboxGroup
              label="Table type"
              options={REFEREE_TYPE_OPTIONS}
              selected={values.refereeTypes}
              onChange={refereeTypes => onChange({ ...values, refereeTypes })}
              disabled={disabled}
            />
            <ConditionCheckboxGroup
              label="Referee type"
              options={REFEREE_CATEGORY_OPTIONS}
              selected={values.refereeCategories}
              onChange={refereeCategories => onChange({ ...values, refereeCategories })}
              disabled={disabled}
            />
          </div>
        </>
      )}
    </div>
  );
}
